import { ErrorOutlined } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import DotElastic from "components/DotElastic";
import Plans from "components/Payment/Plans";
import CancelPlan from "components/Payment/UpgradeFlowComponents/CancelPlan";
import MyPlan from "components/Payment/UpgradeFlowComponents/MyPlan";
import ReviewPurchase from "components/Payment/UpgradeFlowComponents/ReviewPurchase";
import SelectPaymentMethod from "components/Payment/UpgradeFlowComponents/SelectPaymentMethod";
import VerifyEmail from "components/Payment/UpgradeFlowComponents/VerifyEmail";
import { SLAPSHOT_MY_PLAN_URL } from "constants/urls";
import { InvoiceItem, Plan } from "models/Payment";
import { Subscription } from "models/User";
import {
  REVIEW_PURCHASE_INDEX,
  PLANS_INDEX,
  SELECT_PAYMENT_METHOD_INDEX,
  MY_PLAN_INDEX,
  CANCEL_PLAN_INDEX,
  VERIFY_EMAIL_INDEX,
} from "pages/UpgradePlanFlow/UpgradePlanFlow";
import { getUpgradeCharges } from "services/SubscriptionService";

import styles from "./UpgradePlanFlow.module.scss";

export interface UpgradePlanFlowRef {
  setStep: (step: number) => void;
}

interface UpgradePlanFlowProps {
  subscription: Subscription | null;
  workspaceName?: string | null;
}

const UpgradePlanFlow = ({
  subscription,
  workspaceName,
  ref,
}: UpgradePlanFlowProps & { ref?: React.Ref<UpgradePlanFlowRef> }) => {
  const location = useLocation();

  const [step, setStep] = useState(MY_PLAN_INDEX);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[] | null>(null);

  React.useImperativeHandle(ref, () => ({
    setStep(index) {
      setStep(index);
    },
  }));

  const getInvoiceItems = useCallback(() => {
    if (!selectedPlan || !subscription?.id) return;
    getUpgradeCharges(subscription.id, selectedPlan.id).then((items) =>
      setInvoiceItems(items),
    );
  }, [selectedPlan, subscription?.id]);

  useEffect(() => {
    if (selectedPlan) {
      setInvoiceItems(null);
      getInvoiceItems();
    }
  }, [getInvoiceItems, selectedPlan]);

  useEffect(() => {
    if (location.pathname.endsWith("upgrade")) {
      setStep(PLANS_INDEX);
    } else {
      setStep(MY_PLAN_INDEX);
    }
  }, [location.pathname]);

  if (!subscription)
    return (
      <div className={styles.loading}>
        <DotElastic />
      </div>
    );

  return (
    <>
      <div className={styles.header}>
        <h3>
          {[MY_PLAN_INDEX, CANCEL_PLAN_INDEX].includes(step)
            ? `${workspaceName || "My"}${workspaceName ? "'s" : ""} Plan`
            : `Upgrade ${workspaceName || ""}${workspaceName ? "'s" : ""} Plan`}
        </h3>
        {subscription?.is_processing_payment && (
          <div className={styles.processingPayment}>
            <CircularProgress sx={{ color: "#6565ec" }} size={20} />
            <span>We are processing your last transaction!</span>
          </div>
        )}
        {subscription &&
          !subscription?.renewal_date &&
          !subscription?.is_processing_payment && (
            <div className={styles.attentionReq}>
              <ErrorOutlined />
              Your plan expired due to payment failure. Please renew to restore
              access.
            </div>
          )}
      </div>
      <div
        className={cx(
          styles.container,
          location.pathname.startsWith(
            SLAPSHOT_MY_PLAN_URL && styles.containerBg,
          ),
        )}
      >
        {step === MY_PLAN_INDEX && (
          <MyPlan setStep={setStep} subscription={subscription} />
        )}
        {step === PLANS_INDEX && (
          <Plans
            setSelectedPlan={setSelectedPlan}
            setStep={setStep}
            onBack={() => {
              setStep(MY_PLAN_INDEX);
            }}
            subscription={subscription}
          />
        )}

        {step === VERIFY_EMAIL_INDEX && selectedPlan && (
          <VerifyEmail
            plan={selectedPlan}
            onBack={() => {
              setStep(PLANS_INDEX);
            }}
            onVerified={() => {
              setStep(SELECT_PAYMENT_METHOD_INDEX);
            }}
          />
        )}

        {step === SELECT_PAYMENT_METHOD_INDEX && selectedPlan && (
          <SelectPaymentMethod
            invoiceItems={invoiceItems}
            onBack={() => {
              setStep(PLANS_INDEX);
            }}
            setStep={setStep}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            selectedPaymentMethod={selectedPaymentMethod}
            plan={selectedPlan}
            subscription={subscription}
          />
        )}

        {step === REVIEW_PURCHASE_INDEX &&
          selectedPaymentMethod &&
          selectedPlan && (
            <ReviewPurchase
              invoiceItems={invoiceItems}
              setStep={setStep}
              onBack={() => setStep(SELECT_PAYMENT_METHOD_INDEX)}
              plan={selectedPlan}
              paymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              subscription={subscription}
            />
          )}

        {step === CANCEL_PLAN_INDEX && (
          <CancelPlan setStep={setStep} subscription={subscription} />
        )}
      </div>
    </>
  );
};

export default UpgradePlanFlow;
